import { useEffect, useState } from 'react';
import { getArrivagesConfection, getArrivagesConfectionHeritees, getPlanningsSousTraitant, getSousTraitantsConfection, ouvrirSessionConfection } from '../../api/measurementApi';
import ArrivageCard from '../../components/ArrivageCard';
import CarteCommande from '../../components/CarteCommande';
import CarteSousTraitant from '../../components/CarteSousTraitant';
import CommandeImage from '../../components/CommandeImage';
import { Chargement, EtatVide, MessageErreur } from '../../components/EtatListe';
import { BoutonRetour, Capsule, EnTeteSection } from '../../components/ui';
import { hasAnyRole, ROLES_PARC_COMPLET, useAuth } from '../../auth/AuthContext';
import { libelleCommande } from '../../utils/commandeLabel';
import useBreakpoint from '../../utils/useBreakpoint';
import { TEXTE_SECONDAIRE } from '../../theme';

/**
 * Trois niveaux : SOUS-TRAITANT → PLANNING (commande en cours chez lui) → ENVOI DE COUPE. Reflète
 * auditeur-confection-selection.component.ts côté sipconfection-frontend.
 *
 * ⚠️ Le troisième niveau liste ce que la COUPE a envoyé à l'atelier, pas ce qu'il rend : le quota
 * des 10 % porte sur la quantité ENVOYÉE, et l'atelier mesure pendant qu'il confectionne le lot.
 * Les arrivages portent donc `livraisonVersId`. Les campagnes ancrées sur un bon de retour, d'avant
 * ce changement, sont listées à part et seulement pour être TERMINÉES — le serveur refuse d'en
 * créer de nouvelles sur cet ancrage.
 *
 * Un compte OWNER/SUPERADMIN voit tout le parc ; les autres (ST notamment) ne voient que
 * l'atelier dont le `codeUsine` correspond à leur nom de famille Keycloak — même convention que
 * `peutVoir()` dans espace-sous-traitants.component.ts côté sipconfection-frontend.
 */
export default function ConfectionDrilldown({ onSessionOuverte }) {
  const { roles, nomFamille } = useAuth();
  const { compact, large } = useBreakpoint();
  const voitToutLeParc = hasAnyRole(roles, ROLES_PARC_COMPLET);
  const [sousTraitants, setSousTraitants] = useState([]);
  const [loadingST, setLoadingST] = useState(true);
  const [erreurST, setErreurST] = useState(null);

  const [stSelectionne, setStSelectionne] = useState(null);
  const [plannings, setPlannings] = useState([]);
  const [loadingPlannings, setLoadingPlannings] = useState(false);
  const [erreurPlannings, setErreurPlannings] = useState(null);

  const [planningSelectionne, setPlanningSelectionne] = useState(null);
  const [arrivages, setArrivages] = useState([]);
  const [loadingArrivages, setLoadingArrivages] = useState(false);
  const [erreurArrivages, setErreurArrivages] = useState(null);
  const [ouvertureEnCoursId, setOuvertureEnCoursId] = useState(null);
  /**
   * Campagnes HÉRITÉES du planning : celles commencées sur un bon du sous-traitant avant le
   * changement d'ancrage. Listées à part pour qu'une saisie en cours puisse être terminée depuis
   * l'atelier — sans elles, elles ne seraient plus atteignables que depuis l'app de bureau.
   * Un échec est SILENCIEUX : ce n'est qu'un complément, il ne doit pas masquer les lots à mesurer.
   */
  const [heritees, setHeritees] = useState([]);
  // Sens de la dernière navigation : on entre par la droite, on revient par la gauche.
  const [sens, setSens] = useState(null);

  useEffect(() => {
    chargerSousTraitants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function chargerSousTraitants() {
    setLoadingST(true);
    setErreurST(null);
    getSousTraitantsConfection()
      .then((res) => {
        const tous = res || [];
        const visibles = voitToutLeParc ? tous : tous.filter((st) => !!st.codeUsine && st.codeUsine === nomFamille);
        setSousTraitants(visibles);
      })
      .catch((err) => setErreurST(err.message || 'Impossible de charger les sous-traitants.'))
      .finally(() => setLoadingST(false));
  }

  function choisirST(st) {
    setSens('pousser');
    setStSelectionne(st);
    setPlannings([]);
    setPlanningSelectionne(null);
    setArrivages([]);
    setHeritees([]);
    setLoadingPlannings(true);
    setErreurPlannings(null);
    getPlanningsSousTraitant(st.id)
      .then((res) => setPlannings((res || []).filter((p) => !p.cloture)))
      .catch((err) => setErreurPlannings(err.message || 'Impossible de charger les commandes de cet atelier.'))
      .finally(() => setLoadingPlannings(false));
  }

  function choisirPlanning(planning) {
    setSens('pousser');
    setPlanningSelectionne(planning);
    setArrivages([]);
    setHeritees([]);
    setLoadingArrivages(true);
    setErreurArrivages(null);
    getArrivagesConfection(planning.id)
      .then((res) => setArrivages(res || []))
      .catch((err) => setErreurArrivages(err.message || 'Impossible de charger les envois de coupe de ce planning.'))
      .finally(() => setLoadingArrivages(false));
    getArrivagesConfectionHeritees(planning.id)
      .then((res) => setHeritees(res || []))
      .catch(() => setHeritees([]));
  }

  function ouvrirArrivage(arrivage) {
    if (arrivage.sessionId) {
      onSessionOuverte(arrivage.sessionId, { sousTraitant: stSelectionne, planning: planningSelectionne, arrivage });
      return;
    }
    setOuvertureEnCoursId(arrivage.livraisonVersId);
    ouvrirSessionConfection(arrivage.livraisonVersId)
      .then((session) => onSessionOuverte(session.id, { sousTraitant: stSelectionne, planning: planningSelectionne, arrivage, session }))
      .catch((err) => setErreurArrivages(err.message || "Impossible d'ouvrir cette campagne de mesure."))
      .finally(() => setOuvertureEnCoursId(null));
  }

  const classeNav = sens === 'pousser' ? 'aqs-nav-pousser' : sens === 'revenir' ? 'aqs-nav-revenir' : undefined;

  // ----- Niveau 1 : sous-traitants -----
  if (!stSelectionne) {
    return (
      <div key="sous-traitants" className={classeNav}>
        <EnTeteSection titre="Sous-traitants confection" sousTitre="Choisissez l'atelier dont vous mesurez les pièces." />
        <div style={{ marginTop: 22 }}>
          {loadingST && <Chargement texte="Chargement des sous-traitants…" />}
          {erreurST && <MessageErreur texte={erreurST} onReessayer={chargerSousTraitants} />}
          {!loadingST && !erreurST && sousTraitants.length === 0 && <EtatVide texte="Aucun sous-traitant de confection enregistré." />}
          {!loadingST && !erreurST && sousTraitants.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${compact ? 2 : large ? 4 : 3}, minmax(0, 1fr))`, gap: compact ? 12 : 16 }}>
              {sousTraitants.map((st) => (
                <CarteSousTraitant key={st.id} sousTraitant={st} onClick={() => choisirST(st)} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ----- Niveau 2 : plannings -----
  if (!planningSelectionne) {
    return (
      <div key="plannings" className={classeNav}>
        <EnTeteSection
          retour={<BoutonRetour libelle="Sous-traitants" onClick={() => { setSens('revenir'); setStSelectionne(null); setPlannings([]); }} />}
          titre="Commandes en cours"
          sousTitre={`${stSelectionne.name} · choisissez la commande à contrôler.`}
        />
        <div style={{ marginTop: 22 }}>
          {loadingPlannings && <Chargement texte="Chargement des commandes…" />}
          {erreurPlannings && <MessageErreur texte={erreurPlannings} onReessayer={() => choisirST(stSelectionne)} />}
          {!loadingPlannings && !erreurPlannings && plannings.length === 0 && <EtatVide texte="Aucune commande ouverte chez cet atelier." />}
          {!loadingPlannings && !erreurPlannings && plannings.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${large ? 2 : 1}, minmax(0, 1fr))`, gap: 16 }}>
              {plannings.map((p) => {
                const info = libelleCommande(p);
                return (
                  <CarteCommande
                    key={p.id}
                    info={info}
                    identifiantSecours={`#${p.commandeId}`}
                    badge={<Capsule ton="accent">Avant lavage</Capsule>}
                    nbLivraisons={p.livraisonsDepuisSousTraitant?.length}
                    ctaLabel="Voir les livraisons"
                    onClick={() => choisirPlanning(p)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ----- Niveau 3 : envois de coupe -----
  const infoP = libelleCommande(planningSelectionne);
  return (
    <div key="envois" className={classeNav}>
      <EnTeteSection
        retour={<BoutonRetour libelle="Commandes" onClick={() => { setSens('revenir'); setPlanningSelectionne(null); setArrivages([]); setHeritees([]); }} />}
        visuel={<CommandeImage imageUrl={infoP.imageUrl} taille={64} />}
        titre="Envois de coupe à mesurer"
        sousTitre={`Commande ${infoP.numero} · ${infoP.modele} · ${stSelectionne.name}`}
      />

      <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loadingArrivages && <Chargement texte="Chargement des envois de coupe…" />}
        {erreurArrivages && <MessageErreur texte={erreurArrivages} onReessayer={() => choisirPlanning(planningSelectionne)} />}
        {!loadingArrivages && !erreurArrivages && arrivages.length === 0 && <EtatVide texte="La coupe n'a encore rien envoyé sur cette commande." />}
        {!loadingArrivages && !erreurArrivages && arrivages.map((a, i) => (
          <ArrivageCard
            key={a.livraisonVersId ?? a.sessionId ?? i}
            arrivage={a}
            onOuvrir={ouvrirArrivage}
            ouvertureEnCours={ouvertureEnCoursId === a.livraisonVersId}
          />
        ))}
      </div>

      {/* Campagnes commencées AVANT que le contrôle ne passe sur les envois de coupe : elles ne
          sont là que pour être terminées. Toutes portent un `sessionId`, `ouvrirArrivage` part
          donc par sa branche « ouvrir » et n'appelle jamais l'endpoint de création. */}
      {!loadingArrivages && heritees.length > 0 && (
        <div style={{ marginTop: 34 }}>
          <h2 className="aqs-titre-2" style={{ margin: 0 }}>Campagnes à terminer</h2>
          <p className="aqs-para" style={{ margin: '6px 0 0', fontSize: 15, color: TEXTE_SECONDAIRE, maxWidth: 760 }}>
            Commencées sur un bon de livraison, avant que le contrôle ne porte sur les envois de
            coupe. À terminer ici ; les nouvelles mesures se font au-dessus.
          </p>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {heritees.map((a, i) => (
              <ArrivageCard
                key={a.sessionId ?? a.livraisonStId ?? i}
                arrivage={a}
                onOuvrir={ouvrirArrivage}
                ouvertureEnCours={false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
